import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { ClaimTimeline } from './ClaimHistoryTimeline/ClaimTimeline';
import { useGetClaimHistory } from 'api/resource';
import type { Claim, Status, Provider, ClaimChange } from 'interfaces';
import { useTranslation } from '@refinedev/core';
import {
  IconCalendar,
  IconCurrencyDollar,
  IconFilter,
  IconFileText,
  IconHistory,
  IconId,
  IconUser,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { formatDateOnly } from 'utils/dateUtils';
import {
  getDisplayableClaimChanges,
  normalizeChanges,
} from './ClaimHistoryTimeline';
import { formatPatientName } from '@billing/validation';
import { useIsProvider } from 'hooks/useIsProvider';

// Tariff comment history has no visibility flag; tariff comments currently default to internal.
const isProviderHiddenCommentChange = (change: ClaimChange): boolean =>
  change.field === 'internalComments' ||
  (change.type === 'activity' &&
    (change.activity === 'internalCommentAdded' ||
      change.activity === 'internalCommentRemoved' ||
      change.activity === 'tariffCommentAdded'));

export const ClaimHistory = ({
  claimID,
  claim,
  status,
  providers,
}: {
  claimID?: string;
  claim?: Claim | null;
  status: Status[];
  providers: Provider[];
}) => {
  const {
    data: histories = [],
    isLoading: loadingHistory,
    isError: errorHistory,
    error: claimErrorMessage,
  } = useGetClaimHistory({
    claimID: claimID || '',
  });
  const [fieldFilter, setFieldFilter] = useState('All');
  const { translate } = useTranslation();
  const isProvider = useIsProvider();

  const getDefaultFieldLabel = (field: string) =>
    field
      .replace(/ID$/, '')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (char) => char.toUpperCase());

  const getFieldLabel = useCallback(
    (field: string) => {
      switch (field) {
        case 'tariffs':
          return translate(
            'pages:claims.claimHistory.fields.tariffs',
            'Tariffs'
          );
        case 'debits':
          return translate(
            'pages:claims.claimHistory.fields.debits',
            'Payments'
          );
        case 'internalComments':
          return translate(
            'pages:claims.claimHistory.fields.internalComments',
            'Notes'
          );
        case 'provider':
        case 'providerID':
          return translate(
            'pages:claims.claimHistory.fields.provider',
            'Provider'
          );
        case 'patient':
        case 'patientID':
          return translate(
            'pages:claims.claimHistory.fields.patient',
            'Patient'
          );
        case 'healthcard':
        case 'healthcardID':
          return translate(
            'pages:claims.claimHistory.fields.healthcard',
            'Health card'
          );
        case 'payor':
        case 'payorID':
          return translate('pages:claims.claimHistory.fields.payor', 'Payor');
        default:
          return translate(
            `pages:claims.claimHistory.fields.${field}`,
            getDefaultFieldLabel(field)
          );
      }
    },
    [translate]
  );

  const getChangeFieldLabel = useCallback(
    (change: ClaimChange): string => {
      if (
        change.type === 'activity' &&
        (change.activity === 'internalCommentAdded' ||
          change.activity === 'internalCommentRemoved')
      ) {
        return getFieldLabel('internalComments');
      }
      return getFieldLabel(change.field);
    },
    [getFieldLabel]
  );

  const displayableHistories = useMemo(
    () =>
      histories
        .map((history) => ({
          ...history,
          changes: getDisplayableClaimChanges(history.changes).filter(
            (change) => !isProvider || !isProviderHiddenCommentChange(change)
          ),
        }))
        .filter((history) => history.changes.length > 0),
    [histories, isProvider]
  );

  const filterOptions = useMemo(() => {
    const labels = new Set<string>();
    displayableHistories.forEach((history) => {
      history.changes.forEach((change) => {
        labels.add(getChangeFieldLabel(change));
      });
    });
    return ['All', ...Array.from(labels).sort()];
  }, [displayableHistories, getChangeFieldLabel]);

  const filteredHistories = useMemo(() => {
    return displayableHistories
      .map((history) => {
        const keptChanges = history.changes.filter((change) => {
          const label = getChangeFieldLabel(change);
          return fieldFilter === 'All' || label === fieldFilter;
        });

        return {
          ...history,
          changes: keptChanges,
        };
      })
      .filter((history) => normalizeChanges(history.changes).length > 0);
  }, [displayableHistories, fieldFilter, getChangeFieldLabel]);

  const patientName = claim?.patient
    ? formatPatientName(claim.patient.firstName, claim.patient.lastName)
    : 'Unknown patient';

  const phin = claim?.healthcard?.number ?? 'N/A';
  const dob = claim?.patient?.dateOfBirth
    ? formatDateOnly(claim.patient.dateOfBirth, { fallback: 'N/A' })
    : 'N/A';
  const claimNumberDisplay = claim?.claimNumber
    ? `Claim #${claim.claimNumber}`
    : 'Claim #TBD';
  const amountOutstanding = useMemo(() => {
    const billed = (claim?.tariffs ?? []).reduce(
      (sum, tariff) => sum + Number(tariff.total ?? 0),
      0
    );
    const paid = (claim?.debits ?? []).reduce(
      (sum, debit) => sum + Number(debit.paidAmount ?? 0),
      0
    );
    const amount = billed - paid;
    return Number.isFinite(amount)
      ? amount.toLocaleString(undefined, {
          style: 'currency',
          currency: 'CAD',
        })
      : 'N/A';
  }, [claim]);

  return (
    <Stack gap={0} mx="-md" my="-md">
      <Group px="lg" py="md" gap="sm">
        <ThemeIcon variant="light" color="indigo" size={38} radius="md">
          <IconHistory size={20} />
        </ThemeIcon>
        <Box>
          <Title order={3} lh={1.1}>
            Claim History
          </Title>
          <Text size="sm" c="dimmed">
            Audit trail and version history
          </Text>
        </Box>
      </Group>
      <Divider />

      <Box px="lg" py="md">
        <Paper withBorder radius="md" p="lg" shadow="xs" mb="md">
          <Group justify="space-between" align="center" gap="xl" wrap="wrap">
            <Group gap="md" wrap="nowrap" style={{ flex: '1 1 280px' }}>
              <ThemeIcon
                size={54}
                radius="xl"
                variant="light"
                color="indigo"
                style={{ flexShrink: 0 }}
              >
                <IconFileText size={25} />
              </ThemeIcon>
              <Box>
                <Text fw={800} size="xl" lh={1.15}>
                  {claimNumberDisplay}
                </Text>
                <Stack gap="xs">
                  <Group gap="xs" c="dimmed" mt={8} wrap="wrap">
                    <IconUser size={14} />
                    <Text size="sm">{patientName}</Text>
                  </Group>
                  <Group gap="xs" c="dimmed" mt={0} wrap="wrap">
                    <IconCalendar size={14} />
                    <Text size="sm">DOB: {dob}</Text>
                  </Group>
                  <Group gap="xs" c="dimmed" mt={0} wrap="wrap">
                    <IconId size={14} />
                    <Text size="sm">PHIN: {phin}</Text>
                  </Group>
                </Stack>
              </Box>
            </Group>
            <Group gap="md" wrap="wrap" justify="flex-end">
              <Paper withBorder radius="md" p="md" w={238}>
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon radius="xl" variant="default" size={42}>
                    <IconCurrencyDollar size={22} />
                  </ThemeIcon>
                  <Box>
                    <Text size="xs" tt="uppercase" c="dimmed" fw={700} lh={1.1}>
                      Amount
                      <br />
                      Outstanding
                    </Text>
                    <Text fw={800} size="lg" lh={1.15}>
                      {amountOutstanding}
                    </Text>
                  </Box>
                </Group>
              </Paper>
            </Group>
          </Group>
        </Paper>

        <Group gap="sm" mb="md" wrap="nowrap">
          <ActionIcon variant="subtle" color="gray" aria-label="Filter history">
            <IconFilter size={18} />
          </ActionIcon>
          <Group gap="xs" wrap="nowrap">
            {filterOptions.slice(0, 6).map((option) => (
              <Button
                key={option}
                size="xs"
                variant={fieldFilter === option ? 'light' : 'default'}
                color={fieldFilter === option ? 'indigo' : 'gray'}
                onClick={() => setFieldFilter(option)}
              >
                {option}
              </Button>
            ))}
          </Group>
        </Group>
      </Box>

      <Divider />

      <ScrollArea.Autosize mah="65vh" px="lg" py="md">
        <Flex direction="column" justify="flex-start" align="stretch" mb="xs">
          {loadingHistory ? (
            <Loader data-testid="claim-history-loader" />
          ) : filteredHistories.length ? (
            <ClaimTimeline
              data={filteredHistories}
              isLoading={loadingHistory}
              isError={errorHistory}
              errorMessage={claimErrorMessage?.message}
              status={status}
              providers={providers}
            />
          ) : (
            <Paper withBorder radius="md" p="xl" ta="center">
              <Text fw={700}>No matching history found</Text>
              <Text size="sm" c="dimmed">
                Try changing the selected field filter.
              </Text>
            </Paper>
          )}
        </Flex>
      </ScrollArea.Autosize>
    </Stack>
  );
};
